import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fetchTopUsers } from "../../store/actions/userVerifyActions";

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.1 },
    }),
};

function TopUsersPage() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { topUsers, topUsersLoading, topUsersError } = useSelector(
        (state) => state.verify,
    );

    useEffect(() => {
        const deviceToken = localStorage.getItem("deviceToken");
        if (deviceToken) {
            dispatch(fetchTopUsers(deviceToken));
        }
    }, [dispatch]);

    const users =
        topUsers && Array.isArray(topUsers.top_users)
            ? topUsers.top_users.slice(0, 10)
            : [];

    // Intl.DateTimeFormat не всегда даёт полные названия месяцев для "kk"
    // (языка киоска по умолчанию) — берём названия из перевода, а не из Intl.
    const months = t("top_users.months", { returnObjects: true });
    const currentMonth = months[new Date().getMonth()];

    const errorMessage = topUsersError
        ? typeof topUsersError === "string"
            ? topUsersError
            : topUsersError.msg || t("top_users.error_default")
        : null;

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col gap-[1.6vh] bg-[#EDEFF4]">
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className="flex-none bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[2.4vw] py-[2.4vh]"
            >
                <h1 className="text-center text-[2.4vh] font-extrabold tracking-tight">
                    {t("top_users.title")}
                </h1>
                <p className="text-center text-[1.5vh] font-semibold text-[#7B8290] mt-[0.4vh] capitalize">
                    {currentMonth}
                </p>
            </motion.div>

            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className="flex-1 min-h-0 bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[2.4vw] py-[2.4vh] flex flex-col"
            >
                {topUsersLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="w-[5vh] h-[5vh] border-[0.5vh] border-[#E1E4EB] border-t-[#246CE2] rounded-full animate-spin" />
                    </div>
                ) : errorMessage ? (
                    <div className="flex-1 flex items-center justify-center text-center text-[1.7vh] font-semibold text-[#C2543E] px-[2vw]">
                        {errorMessage}
                    </div>
                ) : users.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center text-[1.8vh] font-semibold text-[#7B8290]">
                        {t("routeList.top_users_empty")}
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-[1.2vh]">
                        {users.map((user, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-[1.4vw] bg-[#F1F3F7] rounded-[2vh] px-[1.8vw] py-[1.6vh]"
                            >
                                <span
                                    className={`w-[4vh] h-[4vh] rounded-full flex items-center justify-center text-[1.7vh] font-extrabold flex-none ${
                                        i === 0
                                            ? "bg-[#246CE2] text-white"
                                            : "bg-white text-[#7B8290]"
                                    }`}
                                >
                                    {i + 1}
                                </span>
                                <span className="flex-1 min-w-0 truncate text-[1.9vh] font-semibold">
                                    {user.username}
                                </span>
                                <span className="text-[1.6vh] font-bold text-[#246CE2] flex-none">
                                    {user.total_prints}{" "}
                                    {t("routeList.top_users_pages")}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </motion.div>

            <p className="flex-none text-center text-[1.3vh] font-semibold text-[#7B8290]">
                {t("top_users.footnote")}
            </p>
        </div>
    );
}

export default TopUsersPage;
